import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 40, className = "", ...rest }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: EASE }}
    {...rest}
  >
    {children}
  </motion.div>
);

export const LineReveal = ({ lines, className = "", lineClassName = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <div ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "110%" }}
            animate={inView ? { y: 0 } : { y: "110%" }}
            transition={{ duration: 0.9, delay: delay + i * 0.12, ease: EASE }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </div>
  );
};

export const WordStagger = ({ text, className = "" }) => (
  <motion.p
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-100px" }}
    variants={{ show: { transition: { staggerChildren: 0.025 } } }}
  >
    {text.split(" ").map((w, i) => (
      <motion.span
        key={i}
        className="inline-block"
        variants={{
          hidden: { opacity: 0.12, y: 6 },
          show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } },
        }}
      >
        {w}&nbsp;
      </motion.span>
    ))}
  </motion.p>
);

export const CountUp = ({ value, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const target = Number(value) || 0;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / 1600);
      setN(Math.floor((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);
  return (
    <span ref={ref} className={className}>
      {n.toLocaleString()}
      {suffix}
    </span>
  );
};

export const Marquee = ({ children, className = "", speed = 30, reverse = false }) => (
  <div className={`overflow-hidden whitespace-nowrap ${className}`}>
    <div
      className={`marquee-track ${reverse ? "marquee-reverse" : ""}`}
      style={{ animationDuration: `${speed}s` }}
    >
      <div className="inline-flex shrink-0 items-center">{children}</div>
      <div className="inline-flex shrink-0 items-center" aria-hidden="true">{children}</div>
    </div>
  </div>
);
